import { useAuthStore } from '../store/authStore'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001'

// Helper para obter o token de autenticação
const getAuthHeader = (): HeadersInit => {
  const token = useAuthStore.getState().token
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

export interface KPIsResponse {
  total_leads: number
  total_vendas: number
  total_reservas: number
  vgv: number
  total_pre_cadastros: number
  leads_ativos: number
  taxa_conversao: number
  ticket_medio: number
  variacao: {
    leads: number
    vendas: number
    vgv: number
  }
  periodo: {
    inicio: string
    fim: string
  }
}

export interface EvolucaoItem {
  data: string
  data_completa: string
  leads: number
  vendas: number
  vgv: number
}

export interface EvolucaoMensalItem {
  mes: string
  label: string
  leads: number
  vendas: number
  vgv: number
}

export interface CorretorRanking {
  posicao: number
  id: number
  nome: string
  imobiliaria: string
  time: string
  total_leads: number
  total_pre_cadastros: number
  total_reservas: number
  total_vendas: number
  vgv: number
  taxa_conversao: number
  nota: number
  medalha: string | null
}

export interface FunilItem {
  etapa: string
  total: number
  percentual: number
}

export interface LeadsPorOrigem {
  origem: string
  label: string
  quantidade: number
  percentual: number
}

export interface LeadsPorSituacao {
  situacao: string
  quantidade: number
  percentual: number
}

export interface LeadKanban {
  id: number
  nome: string
  empreendimento: string
  corretor: string
  dias_na_etapa: number
  alerta_sla: boolean
}

export interface CadastroColaboradorPayload {
  idcorretor: string
  nome: string
  ramal: string
  idusuariototalk: string
  equipe: string
}

export interface CadastroColaboradorResponse {
  success: boolean
  message: string
  tipo_erro?: string
  detalhes?: string
  data?: any
}

export interface KanbanColumn {
  id: string
  title: string
  count: number
  leads: LeadKanban[]
}

class ApiService {
  private baseUrl: string

  constructor() {
    this.baseUrl = API_BASE_URL
  }

  private async fetch<T>(endpoint: string, queryParams?: string): Promise<T> {
    const url = queryParams
      ? `${this.baseUrl}${endpoint}?${queryParams}`
      : `${this.baseUrl}${endpoint}`

    const response = await fetch(url, {
      headers: getAuthHeader(),
    })

    // Se não autorizado, redireciona para login
    if (response.status === 401) {
      useAuthStore.getState().logout()
      throw new Error('Sessão expirada. Redirecionando para login...')
    }

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} - ${response.statusText}`)
    }

    return response.json()
  }

  // Overview
  async getKPIs(queryParams: string): Promise<KPIsResponse> {
    return this.fetch<KPIsResponse>('/api/overview/kpis', queryParams)
  }

  async getEvolucaoDiaria(queryParams: string): Promise<EvolucaoItem[]> {
    return this.fetch<EvolucaoItem[]>('/api/overview/evolucao-diaria', queryParams)
  }

  async getEvolucaoMensal(meses: number = 6, tipoNegocio: string = 'global'): Promise<EvolucaoMensalItem[]> {
    return this.fetch<EvolucaoMensalItem[]>(
      '/api/overview/evolucao-mensal',
      `meses=${meses}&tipo_negocio=${tipoNegocio}`
    )
  }

  // Corretores
  async getCorretoresRanking(queryParams: string): Promise<CorretorRanking[]> {
    return this.fetch<CorretorRanking[]>('/api/corretores/ranking', queryParams)
  }

  async getCorretoresLista(idImobiliaria?: number): Promise<any[]> {
    const params = idImobiliaria ? `id_imobiliaria=${idImobiliaria}` : ''
    return this.fetch<any[]>('/api/corretores/lista', params)
  }

  async getImobiliarias(): Promise<any[]> {
    return this.fetch<any[]>('/api/corretores/imobiliarias')
  }

  async getPerformanceTimes(queryParams: string): Promise<any[]> {
    return this.fetch<any[]>('/api/corretores/performance-times', queryParams)
  }

  async getProdutividade(queryParams: string): Promise<any> {
    return this.fetch<any>('/api/corretores/produtividade', queryParams)
  }

  // Vendas / Funil
  async getVendasResumo(queryParams: string): Promise<any> {
    return this.fetch<any>('/api/vendas/resumo', queryParams)
  }

  async getVendasFunil(queryParams: string): Promise<FunilItem[]> {
    return this.fetch<FunilItem[]>('/api/vendas/funil', queryParams)
  }

  async getVendasPorEmpreendimento(queryParams: string): Promise<any[]> {
    return this.fetch<any[]>('/api/vendas/por-empreendimento', queryParams)
  }

  async getVendasPorCorretor(queryParams: string): Promise<any[]> {
    return this.fetch<any[]>('/api/vendas/por-corretor', queryParams)
  }

  async getVendasFunilComparativo(queryParams: string): Promise<any> {
    return this.fetch<any>('/api/vendas/funil-comparativo', queryParams)
  }

  // Leads
  async getLeadsPorOrigem(queryParams: string): Promise<LeadsPorOrigem[]> {
    return this.fetch<LeadsPorOrigem[]>('/api/leads/por-origem', queryParams)
  }

  async getLeadsPorSituacao(queryParams: string): Promise<LeadsPorSituacao[]> {
    return this.fetch<LeadsPorSituacao[]>('/api/leads/por-situacao', queryParams)
  }

  async getLeadsPorEmpreendimento(queryParams: string): Promise<any[]> {
    return this.fetch<any[]>('/api/leads/por-empreendimento', queryParams)
  }

  async getLeadsPorDDD(queryParams: string, limit: number = 10): Promise<any[]> {
    return this.fetch<any[]>('/api/leads/por-ddd', `${queryParams}&limit=${limit}`)
  }

  async getLeadsTemperatura(queryParams: string): Promise<any[]> {
    return this.fetch<any[]>('/api/leads/temperatura', queryParams)
  }

  async getLeadsTempoMedioEtapas(queryParams: string): Promise<any[]> {
    return this.fetch<any[]>('/api/leads/tempo-medio-etapas', queryParams)
  }

  async getLeadsAlertasSLA(queryParams: string): Promise<any[]> {
    return this.fetch<any[]>('/api/leads/alertas-sla', queryParams)
  }

  // Alias para compatibilidade com Overview.tsx
  async getAlertasSLA(queryParams: string): Promise<any> {
    return this.fetch<any>('/api/leads/alertas-sla', queryParams)
  }

  async getLeadsPorTemperatura(queryParams: string): Promise<any[]> {
    return this.fetch<any[]>('/api/leads/temperatura', queryParams)
  }

  async getLeadsKanban(filters?: {
    dataInicio?: string
    dataFim?: string
    tipoNegocio?: string
    idEmpreendimento?: number
    idImobiliaria?: number
    idCorretor?: number
    situacao?: string
    busca?: string
  }): Promise<any> {
    const params = new URLSearchParams()
    if (filters?.dataInicio) params.append('data_inicio', filters.dataInicio)
    if (filters?.dataFim) params.append('data_fim', filters.dataFim)
    if (filters?.tipoNegocio && filters.tipoNegocio !== 'global') params.append('tipo_negocio', filters.tipoNegocio)
    if (filters?.idEmpreendimento) params.append('id_empreendimento', String(filters.idEmpreendimento))
    if (filters?.idImobiliaria) params.append('id_imobiliaria', String(filters.idImobiliaria))
    if (filters?.idCorretor) params.append('id_corretor', String(filters.idCorretor))
    if (filters?.situacao) params.append('situacao', filters.situacao)
    if (filters?.busca) params.append('busca', filters.busca)
    return this.fetch<any>('/api/leads/kanban', params.toString())
  }

  async getLeadsAnalisePerdas(queryParams: string): Promise<any> {
    return this.fetch<any>('/api/leads/analise-perdas', queryParams)
  }

  // Overview - Linhas de Negócio e Times
  async getLinhasNegocio(queryParams: string): Promise<any> {
    return this.fetch<any>('/api/overview/linhas-negocio', queryParams)
  }

  async getTimes(queryParams: string): Promise<any> {
    return this.fetch<any>('/api/overview/times', queryParams)
  }


  async cadastrarColaborador(payload: CadastroColaboradorPayload): Promise<CadastroColaboradorResponse> {
    const response = await fetch(`${this.baseUrl}/api/cadastros/colaboradores`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(payload),
    })

    if (response.status === 401) {
      useAuthStore.getState().logout()
      throw new Error('Sessão expirada. Redirecionando para login...')
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Erro na API: ${response.status}`)
    }

    return response.json()
  }

  // Metas
  async getMetas(filters?: { status?: string; metric_type?: string }): Promise<any[]> {
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.metric_type) params.append('metric_type', filters.metric_type)
    return this.fetch<any[]>('/api/metas/', params.toString())
  }

  async getMetaById(id: number): Promise<any> {
    return this.fetch<any>(`/api/metas/${id}`)
  }

  async createMeta(meta: any): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/metas/`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(meta),
    })
    if (response.status === 401) {
      useAuthStore.getState().logout()
      throw new Error('Sessão expirada')
    }
    if (!response.ok) throw new Error(`API Error: ${response.status}`)
    return response.json()
  }

  async updateMeta(id: number, meta: any): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/metas/${id}`, {
      method: 'PUT',
      headers: getAuthHeader(),
      body: JSON.stringify(meta),
    })
    if (response.status === 401) {
      useAuthStore.getState().logout()
      throw new Error('Sessão expirada')
    }
    if (!response.ok) throw new Error(`API Error: ${response.status}`)
    return response.json()
  }

  async deleteMeta(id: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/metas/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    })
    if (response.status === 401) {
      useAuthStore.getState().logout()
      throw new Error('Sessão expirada')
    }
    if (!response.ok) throw new Error(`API Error: ${response.status}`)
  }

  // Empreendimentos
  async getEmpreendimentosLista(tipoNegocio: string = 'global'): Promise<any[]> {
    return this.fetch<any[]>('/api/empreendimentos/lista', `tipo_negocio=${tipoNegocio}`)
  }

  async getEmpreendimentosSaude(queryParams: string): Promise<any[]> {
    return this.fetch<any[]>('/api/empreendimentos/saude', queryParams)
  }

  async getEmpreendimentoDetalhes(id: number, queryParams: string): Promise<any> {
    return this.fetch<any>(`/api/empreendimentos/${id}`, queryParams)
  }

  // Mídia Paga
  async getMidiaPagaResumo(queryParams: string): Promise<any> {
    return this.fetch<any>('/api/midia-paga/resumo', queryParams)
  }

  // Intersetorial
  async getIntersetorialKPIs(queryParams: string): Promise<any> {
    return this.fetch<any>('/api/intersetorial/kpis', queryParams)
  }

  async getIntersetorialAnaliseCredito(queryParams: string): Promise<any[]> {
    return this.fetch<any[]>('/api/intersetorial/analise-credito', queryParams)
  }

  // Notificações
  async getNotificacoes(): Promise<any> {
    return this.fetch<any>('/api/notificacoes/')
  }
}

export const api = new ApiService()
